/**
 * Mapper functions to convert API-sourced SixFile and SixFolder to FileMetadata
 * for view layer (SixTable) and Helia operations.
 */
import type { SixFile, SixFolder } from '@/types/six/Six';
import type { FileMetadata } from '@/types/poc/helia';

/**
 * Maps a single SixFile (from API/sixDetailSlice) to FileMetadata (for view/Helia).
 *
 * @param file - SixFile from API response
 * @param sixId - The Six ID this file belongs to
 * @returns FileMetadata formatted for SixTable and Helia operations
 */
export function mapSixFileToFileMetadata(file: SixFile, sixId: string): FileMetadata {
  return {
    id: file.id,
    cid: file.cid,
    name: file.name,
    size: file.size,
    type: file.type,
    sixId,
    addedBy: 'unknown', // API doesn't provide uploader info
    lastModified: file.lastModified,
    path: file.path,
    decryptionCIDS: file.encryptedFileKeyCIDS,
    uploadedByUserId: file.uploadedByUserId,
    uploadedByUserName: file.uploadedByUserName,
  };
}

/**
 * Maps an array of SixFile to FileMetadata[].
 *
 * @param files - Array of SixFile from API response
 * @param sixId - The Six ID these files belong to
 * @returns Array of FileMetadata for view layer
 */
export function mapSixFilesToFileMetadata(files: SixFile[], sixId: string): FileMetadata[] {
  return files.map(file => mapSixFileToFileMetadata(file, sixId));
}

/**
 * Maps a SixFolder to FileMetadata for unified table display.
 * Folders are represented as FileMetadata with type: 'folder', size: 0, and empty CID.
 *
 * @param folder - SixFolder from API response
 * @param sixId - The Six ID this folder belongs to
 * @returns FileMetadata formatted for SixTable display
 */
export function mapSixFolderToFileMetadata(folder: SixFolder, sixId: string): FileMetadata {
  return {
    id: folder.id,
    cid: '', // Folders have no content ID
    name: folder.name,
    size: 0, // Folders have no size
    type: 'folder',
    sixId,
    addedBy: folder.createdByUserName,
    lastModified: folder.createdAt,
    path: folder.path,
    decryptionCIDS: [], // Folders have no encryption
    uploadedByUserId: folder.createdByUserId,
    uploadedByUserName: folder.createdByUserName,
  };
}

/**
 * Maps an array of SixFolder to FileMetadata[].
 *
 * @param folders - Array of SixFolder from API response
 * @param sixId - The Six ID these folders belong to
 * @returns Array of FileMetadata for view layer
 */
export function mapSixFoldersToFileMetadata(folders: SixFolder[], sixId: string): FileMetadata[] {
  return folders.map(folder => mapSixFolderToFileMetadata(folder, sixId));
}
